import React, { useState, useEffect } from 'react';
import { Send, MessageCircle, User, Bot } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';

const Support = () => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const { toast } = useToast();
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      loadMessages();
      // Auto refresh every 10 seconds
      const interval = setInterval(loadMessages, 10000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const loadMessages = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/conversations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        }
      });
      const data = await response.json();
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/conversations/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`
        },
        body: JSON.stringify({ message: newMessage })
      });

      if (response.ok) {
        setNewMessage('');
        loadMessages();
        toast({
          title: '✅ Mensagem enviada!',
          description: 'Nossa equipe responderá em breve',
        });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      toast({
        title: '❌ Erro ao enviar',
        description: 'Tente novamente',
        variant: 'destructive'
      });
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-black py-16 px-6 flex items-center justify-center">
        <Card className="bg-gray-900 border-2 border-red-900/30 p-8 text-center">
          <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4 gaming-title">Acesso Restrito</h2>
          <p className="text-gray-400 mb-6">Você precisa estar logado para acessar o suporte</p>
          <Button 
            onClick={() => window.location.href = '/login'}
            className="bg-red-600 hover:bg-red-700 text-white gaming-title"
          >
            Fazer Login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black py-16 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2 gaming-title glow-red">
            SUPORTE TÉCNICO
          </h1>
          <p className="text-xl text-gray-400">
            Chat direto com nossa equipe • Respostas em tempo real
          </p>
        </div>

        {/* Chat Container */}
        <Card className="bg-gray-900 border-2 border-red-900/30">
          <CardHeader className="border-b border-red-900/30 bg-gradient-to-r from-red-600/10 to-red-700/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <span className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 rounded-full border-2 border-[#0D1229]"></span>
                </div>
                <div>
                  <CardTitle className="text-white gaming-title">Chat de Suporte</CardTitle>
                  <p className="text-gray-400 text-sm flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    Equipe online • Resposta rápida
                  </p>
                </div>
              </div>
              <Badge className="bg-green-600/20 text-green-400 border border-green-600/30">
                🟢 Online
              </Badge>
            </div>
          </CardHeader>

          <CardContent className="p-0">
            {/* Messages Area */}
            <div className="h-96 overflow-y-auto p-6 space-y-4">
              {loading ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-gray-400">Carregando conversa...</p>
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <MessageCircle className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-2">Inicie uma conversa</h3>
                  <p className="text-gray-400 text-sm">
                    Nossa equipe está pronta para ajudar você!
                  </p>
                </div>
              ) : (
                messages.map((msg) => (
                  <div key={msg.id} className="space-y-4">
                    {/* User Message */}
                    <div className="flex justify-end">
                      <div className="bg-red-600 text-white rounded-lg rounded-tr-none px-4 py-3 max-w-xs md:max-w-md">
                        <p className="text-sm leading-relaxed">{msg.message}</p>
                        <p className="text-red-200 text-xs mt-1 opacity-80">
                          {new Date(msg.created_at).toLocaleString('pt-BR')}
                        </p>
                      </div>
                      <div className="ml-3 flex items-end">
                        <div className="w-8 h-8 bg-red-600/20 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-red-600" />
                        </div>
                      </div>
                    </div>

                    {/* Admin Reply */}
                    {msg.replied && msg.reply_text && (
                      <div className="flex justify-start">
                        <div className="mr-3 flex items-end">
                          <div className="w-8 h-8 bg-blue-600/20 rounded-full flex items-center justify-center">
                            <Bot className="w-4 h-4 text-blue-600" />
                          </div>
                        </div>
                        <div className="bg-gray-800 text-white rounded-lg rounded-tl-none px-4 py-3 max-w-xs md:max-w-md border border-blue-600/30">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className="bg-blue-600/20 text-blue-400 text-xs">
                              Suporte Cloud Cheats
                            </Badge>
                          </div>
                          <p className="text-sm leading-relaxed">{msg.reply_text}</p>
                          <p className="text-gray-400 text-xs mt-1">
                            {new Date(msg.replied_at).toLocaleString('pt-BR')}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Pending indicator */}
                    {!msg.replied && (
                      <div className="flex justify-start">
                        <div className="mr-3 flex items-end">
                          <div className="w-8 h-8 bg-yellow-600/20 rounded-full flex items-center justify-center">
                            <Bot className="w-4 h-4 text-yellow-600" />
                          </div>
                        </div>
                        <div className="bg-yellow-600/10 text-yellow-400 rounded-lg rounded-tl-none px-4 py-3 max-w-xs md:max-w-md border border-yellow-600/30">
                          <p className="text-sm">
                            ⏳ Nossa equipe está analisando sua mensagem...
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Message Input */}
            <div className="p-6 border-t border-red-900/30 bg-black/50">
              <div className="flex gap-3">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Digite sua mensagem aqui..."
                  rows={3}
                  className="flex-1 bg-gray-800 border border-red-900/30 rounded-lg px-4 py-3 text-white focus:border-red-600 focus:outline-none resize-none placeholder-gray-500"
                />
                <Button
                  onClick={sendMessage}
                  disabled={sending || !newMessage.trim()}
                  className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 self-end"
                >
                  {sending ? (
                    '⏳'
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                💡 Dica: Pressione Enter para enviar ou Shift+Enter para nova linha
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Quick Help */}
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <Card className="bg-gray-900 border border-red-900/20">
            <CardContent className="p-4">
              <h3 className="text-white font-bold mb-2">🚀 Problemas com cheats?</h3>
              <p className="text-gray-400 text-sm">
                Descreva o problema detalhadamente e nossa equipe ajudará você
              </p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border border-red-900/20">
            <CardContent className="p-4">
              <h3 className="text-white font-bold mb-2">💳 Questões de pagamento?</h3>
              <p className="text-gray-400 text-sm">
                Envie seu comprovante ou ID do pedido para verificarmos
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Support;